import React, { useContext, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AuthContext } from '../auth/AuthContext';
import api from '../services/api';
import { getRole, getToken, getUser, updateStoredUser } from '../services/auth';
import { adminAccessRedirect, validateAdminPasswordChange } from '../utils/adminPasswordPolicy';
import './AdminPasswordChange.css';

const useResolvedAuth = () => {
  const context = useContext(AuthContext) || {};
  return {
    context,
    token: context.token || getToken(),
    user: context.user || getUser(),
    role: context.role || getRole(),
  };
};

export function RequireAdminPasswordReady({ children }) {
  const { token, user, role } = useResolvedAuth();
  const redirect = adminAccessRedirect({ token, user, role });
  if (redirect) return <Navigate to={redirect} replace />;
  return children;
}

export function PasswordChangeOnly({ children }) {
  const { token, user, role } = useResolvedAuth();
  const redirect = adminAccessRedirect({ token, user, role, passwordScreen: true });
  if (redirect) return <Navigate to={redirect} replace />;
  return children;
}

export default function AdminPasswordChange() {
  const navigate = useNavigate();
  const { context, user } = useResolvedAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    const validationError = validateAdminPasswordChange({ currentPassword, newPassword, confirmPassword });
    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    try {
      await api.post(`/users/${user._id}/change-password`, { currentPassword, newPassword });
      const updatedUser = { ...user, mustChangePassword: false };
      updateStoredUser(updatedUser);
      context.setUser?.(updatedUser);
      navigate('/admin', { replace: true });
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'Unable to change your password. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="admin-password-page">
      <section className="admin-password-card" aria-labelledby="password-change-title">
        <img src="/lms-logo.png" alt="MyApex LMS" />
        <p className="admin-password-eyebrow">SECURE ADMIN ACCESS</p>
        <h1 id="password-change-title">Create a new password</h1>
        <p className="admin-password-intro">
          Replace your temporary password before continuing to MyAdmin.
        </p>
        <form onSubmit={submit}>
          <label>
            Temporary password
            <input
              type={showPasswords ? 'text' : 'password'}
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
            />
          </label>
          <label>
            New password
            <input
              type={showPasswords ? 'text' : 'password'}
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              minLength={12}
              required
            />
          </label>
          <label>
            Confirm new password
            <input
              type={showPasswords ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              minLength={12}
              required
            />
          </label>
          <label className="admin-password-show">
            <input
              type="checkbox"
              checked={showPasswords}
              onChange={(event) => setShowPasswords(event.target.checked)}
            />
            Show passwords
          </label>
          <p className="admin-password-help">Use at least 12 characters with a mix of letters, numbers and symbols.</p>
          {error && <p className="admin-password-error" role="alert">{error}</p>}
          <button type="submit" disabled={busy}>
            {busy ? 'Updating password...' : 'Update password and continue'}
          </button>
        </form>
      </section>
    </main>
  );
}
